import { ReactElement } from 'react'
import styled from 'styled-components'
import { colors, radius } from '../tokens'

export type ProgressTone = 'positive' | 'warning' | 'danger'

export const occupancyTone = (percent: number): ProgressTone => (percent >= 100 ? 'danger' : percent >= 90 ? 'warning' : 'positive')
type ProgressTrack = 'white' | 'accent' | 'neutral'

const TRACKS: Record<ProgressTrack, string> = { white: colors.white, accent: colors.accent, neutral: colors.border }
const FILLS: Record<ProgressTone, string> = { positive: colors.positive, warning: colors.warning, danger: colors.danger }

const Track = styled.div<{ $track: ProgressTrack, $height: number }>`
  flex: 1 0 0;
  min-width: 0;
  height: ${({ $height }) => $height}px;
  min-height: ${({ $height }) => $height}px;
  border-radius: ${radius.progress};
  overflow: hidden;
  background: ${({ $track }) => TRACKS[$track]};
`

const Fill = styled.div<{ $tone: ProgressTone }>`
  height: 100%;
  border-radius: ${radius.progress};
  background: ${({ $tone }) => FILLS[$tone]};
`

interface ProgressBarProps {
  percentage: number
  label: string
  track?: ProgressTrack
  tone?: ProgressTone
  height?: number
  valueText?: string
}

export function ProgressBar({
  percentage,
  label,
  track = 'white',
  tone = 'positive',
  height = 6,
  valueText,
}: ProgressBarProps): ReactElement {
  const value = Math.max(0, Math.round(percentage * 10) / 10)
  const width = Math.min(100, Math.max(0, percentage))
  return (
    <Track
      $track={track}
      $height={height}
      role="progressbar"
      aria-label={label}
      aria-valuetext={valueText ?? label}
      aria-valuemin={0}
      aria-valuemax={Math.max(100, value)}
      aria-valuenow={value}
    >
      <Fill $tone={tone} data-testid="progress-fill" style={{ width: `${width}%` }} />
    </Track>
  )
}
